import { Flex, Box, DataList, Separator, Table } from '@volue/wave-react';
import React from 'react';

import { EvaluatedResultComponent } from '@/features/execute-calculation-set/components/EvaluatedResultComponent';
import type { SelectedGridElement } from '@/features/execute-calculation-set/overmind/calculation';
import { isCalculationTacticResultUnavailable } from '@/features/execute-calculation-set/overmind/calculation/actions';
import { useAppState } from '@/overmind';

type SelectedEnergyConsumer = {
  type: 'SELECTED_GRID_ELEMENT';
  gridElement: SelectedGridElement<'EnergyConsumer'>;
};

export const EnergyConsumerDetails: React.FunctionComponent<{
  energyConsumer: SelectedEnergyConsumer;
}> = ({ energyConsumer }) => {
  const tacticResultBrowsingFilter =
    useAppState().calculation.tacticResultBrowsingFilter;
  const getLoadSourceValue = (loadSource?: string) =>
    loadSource == null ? '' : `(${loadSource})`;

  const calculationTactic = useAppState().calculation.calculationTactic.value;
  if (isCalculationTacticResultUnavailable(calculationTactic)) return null;
  const voltagelevel = calculationTactic.calculationSetResult.voltageLevel;

  return (
    <>
      {(tacticResultBrowsingFilter.type === 'ALL' ||
        tacticResultBrowsingFilter.type === 'ALL_LOAD_FLOW' ||
        (tacticResultBrowsingFilter.type === 'SELECTED_LOAD_FLOW' &&
          tacticResultBrowsingFilter.scenarioIds.length > 0)) &&
        energyConsumer.gridElement.loadflow.type ===
          'ALL_LOAD_FLOW_SCENARIO_DETAILS' && (
          <>
            <Table aria-label="Load scenarios" highlightOnHover={false}>
              <Table.Header>
                <Table.ColumnHeader>{'Scenario'}</Table.ColumnHeader>
                <>
                  {energyConsumer.gridElement.loadflow.details.map(scenario => (
                    <Table.ColumnHeader>
                      {scenario.scenarioName}
                    </Table.ColumnHeader>
                  ))}
                </>
              </Table.Header>
              <Table.Body>
                <Table.Row>
                  <Flex
                    as={Table.Cell}
                    flow="column"
                    gap={'none'}
                    justifyContent="start"
                    alignItems="start"
                  >
                    <Box>Voltage</Box>
                  </Flex>
                  <>
                    {energyConsumer.gridElement.loadflow.details.map(
                      scenario => (
                        <Table.Cell>
                          <EvaluatedResultComponent
                            result={scenario.scenarioDetails.results.voltage}
                          />
                        </Table.Cell>
                      )
                    )}
                  </>
                </Table.Row>
                <Table.Row>
                  <Flex
                    as={Table.Cell}
                    flow="column"
                    gap={'none'}
                    justifyContent="start"
                    alignItems="start"
                  >
                    <Box>Voltage Drop</Box>
                  </Flex>
                  <>
                    {energyConsumer.gridElement.loadflow.details.map(
                      scenario => (
                        <Table.Cell>
                          <EvaluatedResultComponent
                            result={
                              scenario.scenarioDetails.results
                                .voltageDiffPercent
                            }
                            unitLabel
                          />
                        </Table.Cell>
                      )
                    )}
                  </>
                </Table.Row>
                <Table.Row>
                  <Flex
                    as={Table.Cell}
                    flow="column"
                    gap={'none'}
                    justifyContent="start"
                    alignItems="start"
                  >
                    <Box>Power Factor</Box>
                  </Flex>
                  <>
                    {energyConsumer.gridElement.loadflow.details.map(
                      scenario => (
                        <Table.Cell>
                          <EvaluatedResultComponent
                            result={
                              scenario.scenarioDetails.results.voltageCosinePhi
                            }
                          />
                        </Table.Cell>
                      )
                    )}
                  </>
                </Table.Row>
              </Table.Body>
            </Table>
            <Separator />
            <Table aria-label="Load scenarios" highlightOnHover={false}>
              <Table.Body>
                <Table.Row>
                  <Flex
                    as={Table.Cell}
                    flow="column"
                    gap={'none'}
                    justifyContent="start"
                    alignItems="start"
                  >
                    <Box>Active Power</Box>
                  </Flex>
                  <>
                    {energyConsumer.gridElement.loadflow.details.map(
                      scenario => (
                        <Table.Cell className="dIB">
                          <Box>
                            {scenario.scenarioDetails.inputs.p.toString()}
                          </Box>
                          <Box>
                            {getLoadSourceValue(
                              scenario.scenarioDetails.inputs.loadSource
                            )}
                          </Box>
                        </Table.Cell>
                      )
                    )}
                  </>
                </Table.Row>
                <Table.Row>
                  <Flex
                    as={Table.Cell}
                    flow="column"
                    gap={'none'}
                    justifyContent="start"
                    alignItems="start"
                  >
                    <Box>Reactive Power</Box>
                  </Flex>
                  <>
                    {energyConsumer.gridElement.loadflow.details.map(
                      scenario => (
                        <Table.Cell className="dIB">
                          <Box>
                            {scenario.scenarioDetails.inputs.q.toString()}
                          </Box>
                          <Box>
                            {getLoadSourceValue(
                              scenario.scenarioDetails.inputs.loadSource
                            )}
                          </Box>
                        </Table.Cell>
                      )
                    )}
                  </>
                </Table.Row>
              </Table.Body>
            </Table>
          </>
        )}
      {(tacticResultBrowsingFilter.type === 'ALL' ||
        tacticResultBrowsingFilter.type === 'SHORT_CIRCUIT') &&
        energyConsumer.gridElement.shortCircuit.type ===
          'SHORT_CIRCUIT_DETAILS' && (
          <>
            {energyConsumer.gridElement.shortCircuit.type ===
              'SHORT_CIRCUIT_DETAILS' && (
              <Flex flow="column" gap={'spacingXs'}>
                <Box css={{ color: '$colorAccent70', fontWeight: 'bolder' }}>
                  {'Short Circuit'}
                </Box>
                {energyConsumer.gridElement.objectType === 'EnergyConsumer' &&
                  energyConsumer.gridElement.shortCircuit.evaluatedResults
                    .objectType === 'EnergyConsumer' && (
                    <DataList className="mHxl" ratio={[3, 1]}>
                      {voltagelevel === 'LowVoltage' && (
                        <>
                          {energyConsumer.gridElement.shortCircuit
                            .evaluatedResults.results.protectingReleaseTime
                            .type === 'CALCULATION_RESULT_AVAILABLE' && (
                            <Flex as={DataList.Row} main="space-between">
                              <DataList.Label>
                                {'Protecting release time'}
                              </DataList.Label>
                              <Flex main={'end'} as={DataList.Value}>
                                <EvaluatedResultComponent
                                  result={
                                    energyConsumer.gridElement.shortCircuit
                                      .evaluatedResults.results
                                      .protectingReleaseTime.result
                                  }
                                />
                              </Flex>
                            </Flex>
                          )}
                          {energyConsumer.gridElement.shortCircuit
                            .evaluatedResults.results.protectingReleaseTime
                            .type === 'CALCULATION_RESULT_NOT_AVAILABLE' && (
                            <Flex as={DataList.Row} main="space-between">
                              <DataList.Label>
                                {'Protecting release time'}
                              </DataList.Label>
                              <Flex main={'end'} as={DataList.Value}>
                                {'N/A'}
                              </Flex>
                            </Flex>
                          )}
                        </>
                      )}

                      {energyConsumer.gridElement.shortCircuit.evaluatedResults
                        .results.phase1Min.type ===
                        'CALCULATION_RESULT_AVAILABLE' && (
                        <Flex as={DataList.Row} main="space-between">
                          <DataList.Label>{'Phase1Min'}</DataList.Label>
                          <Flex main={'end'} as={DataList.Value}>
                            <EvaluatedResultComponent
                              result={
                                energyConsumer.gridElement.shortCircuit
                                  .evaluatedResults.results.phase1Min.result
                              }
                            />
                          </Flex>
                        </Flex>
                      )}
                      {energyConsumer.gridElement.shortCircuit.evaluatedResults
                        .results.phase1Max.type ===
                        'CALCULATION_RESULT_AVAILABLE' && (
                        <Flex as={DataList.Row} main="space-between">
                          <DataList.Label>{'Phase1Max'}</DataList.Label>
                          <Flex main={'end'} as={DataList.Value}>
                            <EvaluatedResultComponent
                              result={
                                energyConsumer.gridElement.shortCircuit
                                  .evaluatedResults.results.phase1Max.result
                              }
                            />
                          </Flex>
                        </Flex>
                      )}
                      {energyConsumer.gridElement.shortCircuit.evaluatedResults
                        .results.phase2Min.type ===
                        'CALCULATION_RESULT_AVAILABLE' && (
                        <Flex as={DataList.Row} main="space-between">
                          <DataList.Label>{'Phase2Min'}</DataList.Label>
                          <Flex main={'end'} as={DataList.Value}>
                            <EvaluatedResultComponent
                              result={
                                energyConsumer.gridElement.shortCircuit
                                  .evaluatedResults.results.phase2Min.result
                              }
                            />
                          </Flex>
                        </Flex>
                      )}
                      {energyConsumer.gridElement.shortCircuit.evaluatedResults
                        .results.phase3Max.type ===
                        'CALCULATION_RESULT_AVAILABLE' && (
                        <Flex as={DataList.Row} main="space-between">
                          <DataList.Label>{'Phase3Max'}</DataList.Label>
                          <Flex main={'end'} as={DataList.Value}>
                            <EvaluatedResultComponent
                              result={
                                energyConsumer.gridElement.shortCircuit
                                  .evaluatedResults.results.phase3Max.result
                              }
                            />
                          </Flex>
                        </Flex>
                      )}
                    </DataList>
                  )}
              </Flex>
            )}
          </>
        )}
    </>
  );
};
